import { useEffect, useRef, useState } from "react";
import { faqItems } from "@/mocks/faq";
import { SITE } from "@/lib/site";

export default function FAQSection() {
  const ref = useRef<HTMLDivElement>(null);
  const [openIndex, setOpenIndex] = useState<number | null>(0);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          el.classList.add("opacity-100", "translate-y-0");
          el.classList.remove("opacity-0", "translate-y-6");
          observer.disconnect();
        }
      },
      { threshold: 0.1 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <section
      id="faq"
      className="py-14 md:py-20 lg:py-24 px-4 md:px-6 lg:px-10 bg-secondary-900"
    >
      <div
        ref={ref}
        className="max-w-7xl mx-auto opacity-0 translate-y-6 transition-all duration-700 ease-out"
      >
        <div className="flex flex-col lg:flex-row gap-8 lg:gap-12">
          <div className="lg:w-[35%] flex-shrink-0">
            <span className="inline-block text-xs font-medium text-background-400 tracking-widest uppercase border border-background-700/60 px-3 py-1 rounded-full mb-4">
              FAQ
            </span>
            <h2 className="font-heading text-2xl md:text-3xl lg:text-4xl font-semibold text-background-50 leading-tight mb-4">
              Frequently
              <br />
              <span className="italic text-primary-300 font-normal">
                Asked Questions
              </span>
            </h2>
            <p className="text-background-400 text-sm leading-relaxed mb-6">
              Can&rsquo;t find the answer you&rsquo;re looking for? Don&rsquo;t
              hesitate to contact us directly.
            </p>
            <a
              href={`tel:${SITE.phone}`}
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap"
            >
              <i className="ri-phone-fill" />
              <span>{SITE.phoneDisplay}</span>
            </a>
          </div>

          <div className="lg:w-[65%]" itemScope itemType="https://schema.org/FAQPage">
            <div className="space-y-2">
              {faqItems.map((item, idx) => (
                <div
                  key={item.id}
                  itemScope
                  itemType="https://schema.org/Question"
                  className="bg-secondary-800/50 rounded-xl border border-background-700/40 overflow-hidden"
                >
                  <button
                    onClick={() => setOpenIndex(openIndex === idx ? null : idx)}
                    className="w-full flex items-center justify-between px-4 md:px-5 py-3.5 md:py-4 text-left"
                  >
                    <span className="text-background-50 text-sm md:text-base font-medium pr-4">
                      {item.question}
                    </span>
                    <div className="w-7 h-7 flex items-center justify-center rounded-full bg-primary-500/20 text-primary-300 flex-shrink-0">
                      <i
                        className={`ri-${
                          openIndex === idx ? "subtract" : "add"
                        }-line text-sm`}
                      />
                    </div>
                  </button>
                  <div
                    className={`overflow-hidden transition-all duration-300 ${
                      openIndex === idx ? "max-h-96" : "max-h-0"
                    }`}
                  >
                    <div
                      itemScope
                      itemType="https://schema.org/Answer"
                      className="px-4 md:px-5 pb-4 md:pb-5 pt-0"
                    >
                      <p className="text-background-400 text-sm leading-relaxed">
                        {item.answer}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}